import React, { useCallback, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListOnItemsRenderedProps, VariableSizeList } from 'react-window';

import NoMatchOnSearch from 'flow/components/nav/empty-states/NoMatchOnSearch';
import {
    firstIndexToFocus,
    listItemKey,
    uniqueRowKeys,
} from 'flow/components/nav/list-keys/list-key-action';
import useListTabStop from 'flow/components/nav/list-keys/useListTabStop';
import Loading from 'flow/components/ui-components/Loading';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensionsAreLoading } from 'flow/selectors/contact-list-selectors';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { saveDepartmentState } from 'flow/utils/browser-utils';
import {
    contactListRowHeight,
    contactListRowKey,
    rowIsFocusable,
} from './contact-list-rows';
import { ContactListItemData } from './contact-list-item-data';
import ContactListRow from './ContactListRow';
import useContactListRows from './useContactListRows';
import useContactListSelection, {
    OnItemSelected,
} from './useContactListSelection';
import useFetchContactListData from './useFetchContactListData';
import useFocusListOnReturn from './useFocusListOnReturn';

interface Props {
    selected?: string;
    extensionFilter?: string;
    showActionButtons?: boolean;
    disableItemsWithoutNumber?: boolean;
    onItemSelected: OnItemSelected;
    showOverviewRow?: boolean;
    /**
     * The selection follows the contact with the visible focus ring, else it
     * is the open page's contact. Set it to false where only Enter or a click
     * makes the selection. An example is a picker, where the selection is the
     * Accept target.
     */
    selectionFollowsFocus?: boolean;
    /**
     * In the narrow layout a contact card hides the list. When the list shows
     * again, focus goes back to the row that the user left. Set it to false
     * where no contact card hides the list.
     */
    refocusOnReturn?: boolean;
}

const ContactList = ({
    selected,
    extensionFilter = '',
    showActionButtons = true,
    disableItemsWithoutNumber = false,
    onItemSelected,
    showOverviewRow = true,
    selectionFollowsFocus = true,
    refocusOnReturn = true,
}: Props) => {
    const { i18next } = useAppContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<VariableSizeList>(null);
    const [sectionOpenStates, setSectionOpenStates] = useState<
        Record<string, boolean>
    >({});

    useFetchContactListData();

    const isLoading = useAppSelector(selectExtensionsAreLoading);
    const rows = useContactListRows({
        searchText: extensionFilter,
        showOverviewRow,
        sectionOpenStates,
    });

    // react-window keys each row element by its key, so focus and the row's
    // own state, such as an open menu, stay with its contact when the list
    // sorts again. The Tab stop and the highlight name their row by it too.
    const rowKeys = useMemo(
        () => uniqueRowKeys(rows, contactListRowKey),
        [rows],
    );

    const {
        selectionStore,
        openRowStore,
        findEntryIndex,
        onRowClick,
        onRowFocus,
        onFocusLeave,
    } = useContactListSelection({
        rows,
        rowKeys,
        selected,
        searchText: extensionFilter,
        onItemSelected,
        selectionFollowsFocus,
    });

    const rowToFocus = useCallback(() => {
        const entryIndex = findEntryIndex();
        const entryRowIsFocusable =
            entryIndex !== -1 &&
            rows[entryIndex] !== undefined &&
            rowIsFocusable(rows[entryIndex]);
        return entryRowIsFocusable
            ? entryIndex
            : firstIndexToFocus(rows, rowIsFocusable);
    }, [rows, findEntryIndex]);
    const focusPendingRow = useFocusListOnReturn({
        isEnabled: refocusOnReturn,
        containerRef,
        listRef,
        rowToFocus,
    });

    const {
        tabStopStore,
        onFocus,
        onBlur,
        onKeyDown,
        onItemsRendered,
        onRowUnmountWithFocus,
    } = useListTabStop({
        containerRef,
        listRef,
        rows,
        rowKeys,
        rowIsFocusable,
        entryIndex: rowToFocus,
        onRowFocus,
        onFocusLeave,
    });
    const onRowsRendered = useCallback(
        (renderedRows: ListOnItemsRenderedProps) => {
            focusPendingRow();
            onItemsRendered(renderedRows);
        },
        [focusPendingRow, onItemsRendered],
    );

    const onToggleSection = useCallback(
        (sectionId: string, isOpen: boolean) => {
            saveDepartmentState(sectionId, !isOpen);
            setSectionOpenStates((openStates) => ({
                ...openStates,
                [sectionId]: !isOpen,
            }));
        },
        [],
    );

    const itemData = useMemo<ContactListItemData>(
        () => ({
            rows,
            searchText: extensionFilter,
            rowKeys,
            selectionStore,
            openRowStore,
            marksOpenPage: selectionFollowsFocus,
            tabStopStore,
            showActionButtons,
            disableItemsWithoutNumber,
            onRowClick,
            onToggleSection,
            onRowUnmountWithFocus,
        }),
        [
            rows,
            extensionFilter,
            rowKeys,
            selectionStore,
            openRowStore,
            selectionFollowsFocus,
            tabStopStore,
            showActionButtons,
            disableItemsWithoutNumber,
            onRowClick,
            onToggleSection,
            onRowUnmountWithFocus,
        ],
    );

    if (isLoading) {
        return <Loading />;
    }

    // react-window never clears its row height cache on its own.
    // https://react-window.now.sh/#/api/VariableSizeList
    if (listRef.current) {
        listRef.current.resetAfterIndex(0, false);
    }

    const searchFoundNothing =
        !isEmptyNullOrUndefined(extensionFilter) && rows.length === 0;

    return (
        <div // NOSONAR
            // Not a control: see useListTabStop.
            ref={containerRef}
            className="z-20 flex h-full grow flex-col"
            data-context-menu="false"
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
        >
            <div className="flex h-full flex-1 flex-col">
                <div className="flex flex-1">
                    {searchFoundNothing ? (
                        <NoMatchOnSearch reserveScrollbarGutter={true}>
                            {i18next.t('Colleagues.SearchEmpty.Description', {
                                filterText: extensionFilter,
                            })}
                        </NoMatchOnSearch>
                    ) : (
                        <AutoSizer
                            className="left-nav-scroll-wrapper size-full"
                            disableWidth={true}
                        >
                            {({ height }) => (
                                <VariableSizeList
                                    ref={listRef}
                                    overscanCount={7}
                                    height={height}
                                    width="100%"
                                    itemData={itemData}
                                    itemKey={listItemKey}
                                    itemCount={rows.length}
                                    itemSize={(index) =>
                                        contactListRowHeight(rows[index])
                                    }
                                    onItemsRendered={onRowsRendered}
                                    className="scrollbar left-nav-scroll relative w-full"
                                    style={{
                                        overflowY: 'scroll',
                                        direction: 'inherit',
                                    }}
                                >
                                    {ContactListRow}
                                </VariableSizeList>
                            )}
                        </AutoSizer>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactList;
